import * as React from 'react';
import './LoginForm.css';
import { Link } from 'react-router-dom';

interface Props {
  onEmailChange: (event: React.FormEvent<HTMLInputElement>) => void;
  onPasswordChange: (event: React.FormEvent<HTMLInputElement>) => void;
  onRememberMeChange: (event: React.FormEvent<HTMLInputElement>) => void;
  onFormSubmit: (event: React.SyntheticEvent) => void;
  email: string;
  password: string;
  isRememberMe: boolean;
  isError: boolean;
}

const Icon: React.FC<{ path: string; size?: number; strokeWidth?: number }> = ({
  path,
  size = 18,
  strokeWidth = 1.8
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={path} />
  </svg>
);

const ICON = {
  mail: 'M4 6h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1zM3.5 7l8.5 6 8.5-6',
  lock: 'M6 11V8a6 6 0 0 1 12 0v3M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  eyeOff:
    'M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.4 5.2A9.5 9.5 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3 3.8M6.2 6.2A17 17 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3.3-.6',
  alert: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  shield: 'M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z',
  arrow: 'M5 12h14M13 6l6 6-6 6'
};

export function LoginForm(props: Props) {
  const {
    onFormSubmit,
    isError,
    onEmailChange,
    email,
    password,
    onPasswordChange,
    isRememberMe,
    onRememberMeChange
  } = props;
  const [isPasswordVisible, setPasswordVisible] = React.useState(false);

  return (
    <div className="sitLoginPage">
      <div className="sitLoginCard">
        <aside className="sitLoginHero">
          <img src="/dist/images/logoImage.png" alt="Squad In Touch" />
          <h2>The home ground for school life.</h2>
          <p>From important communications to clubs, sport and events — your whole school community in one place.</p>
          <div className="sitLoginHero__points">
            <div>
              <div>
                <strong>Communicate clearly</strong>
                <em>Important messages, forms and consent, reaching the right people.</em>
              </div>
            </div>
            <div>
              <div>
                <strong>Bring everything together</strong>
                <em>Clubs, sport, fixtures, events, awards and everyday school activities.</em>
              </div>
            </div>
            <div>
              <div>
                <strong>Save time across the school</strong>
                <em>One connected platform for staff, parents and pupils.</em>
              </div>
            </div>
          </div>
        </aside>

        <main className="sitLoginFormPane">
          <form className="sitLoginForm" onSubmit={onFormSubmit}>
            <h1>Sign in</h1>
            <p>
              New to Squad In Touch? <Link to={{ pathname: '/registration' }}>Get started</Link>
            </p>

            {isError && (
              <div className="sitLoginError">
                <Icon path={ICON.alert} size={16} strokeWidth={2} />
                Incorrect email or password. Please try again.
              </div>
            )}

            <label className={isError ? 'sitLoginField sitLoginField--error' : 'sitLoginField'}>
              <span>Email address</span>
              <div>
                <Icon path={ICON.mail} size={17} />
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={onEmailChange}
                  id="login_email"
                />
              </div>
            </label>

            <label className={isError ? 'sitLoginField sitLoginField--error' : 'sitLoginField'}>
              <span>
                Password
                <Link to={{ pathname: '/resetPassword' }}>Forgot password?</Link>
              </span>
              <div>
                <Icon path={ICON.lock} size={17} />
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  placeholder="Enter your password"
                  id="login_password"
                  value={password}
                  onChange={onPasswordChange}
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible(value => !value)}
                  aria-label="Toggle password visibility"
                >
                  <Icon path={isPasswordVisible ? ICON.eyeOff : ICON.eye} size={17} />
                </button>
              </div>
            </label>

            <label className="sitLoginRemember">
              <input type="checkbox" checked={isRememberMe} onChange={onRememberMeChange} />
              <span>Keep me signed in</span>
            </label>

            <button className="sitLoginSubmit" type="submit" id="login_submit">
              Sign in
              <Icon path={ICON.arrow} size={17} />
            </button>

            <div className="sitLoginSecurity">
              <Icon path={ICON.shield} size={14} />
              Protected by verified, encrypted sign-in.
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}
